import { fetchNotificationPreferences, updateNotificationPreferences } from '@api/useQueryCalls';
import { Text, View } from '@components/primitives';
import { useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, Switch } from 'react-native';
import { useTranslation } from 'react-i18next';
import styles from './styles';

type NotificationSettingKey =
  | 'pushNotifications'
  | 'transactionNotifications'
  | 'ewaStatusNotifications'
  | 'securityNotifications';

export interface NotificationPreferencesResponse {
  ewaStatusNotifications: boolean;
  pushNotifications: boolean;
  securityNotifications: boolean;
  transactionNotifications: boolean;
}

export const INITIAL_NOTIFICATION_SETTINGS: NotificationPreferencesResponse = {
  pushNotifications: true,
  transactionNotifications: true,
  ewaStatusNotifications: true,
  securityNotifications: true,
};

export const updateNotificationSetting = (
  previousSettings: NotificationPreferencesResponse,
  key: NotificationSettingKey,
  value: boolean,
): NotificationPreferencesResponse => {
  if (key === 'securityNotifications') return previousSettings;

  return {
    ...previousSettings,
    [key]: value,
    securityNotifications: true,
  };
};

export const getUpdateNotificationPreferencesPayload = (settings: NotificationPreferencesResponse) => ({
  pushNotifications: settings.pushNotifications,
  transactionNotifications: settings.transactionNotifications,
  ewaStatusNotifications: settings.ewaStatusNotifications,
});

export const getNotificationPreferencesStateFromResponse = (
  notificationPreferences?: Partial<NotificationPreferencesResponse>,
) => {
  if (!notificationPreferences) return null;

  return {
    pushNotifications: Boolean(notificationPreferences.pushNotifications),
    transactionNotifications: Boolean(notificationPreferences.transactionNotifications),
    ewaStatusNotifications: Boolean(notificationPreferences.ewaStatusNotifications),
    securityNotifications: true,
  };
};

interface SettingsSwitchProps {
  disabled?: boolean;
  isLoading?: boolean;
  onValueChange: (value: boolean) => void;
  testID: string;
  value: boolean;
}

function SettingsSwitch({
  disabled = false,
  isLoading = false,
  onValueChange,
  testID,
  value,
}: SettingsSwitchProps): React.JSX.Element {
  if (isLoading) {
    return (
      <View style={styles.switchLoaderContainer} testID={`${testID}-loader`}>
        <ActivityIndicator color={colors.secondary.orchid} size="small" />
      </View>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <Pressable
        accessibilityRole="switch"
        accessibilityState={{ checked: value, disabled }}
        disabled={disabled}
        onPress={() => onValueChange(!value)}
        style={[
          styles.webSwitchTrack,
          value ? styles.webSwitchTrackActive : null,
          disabled ? styles.webSwitchTrackDisabled : null,
        ]}
        testID={testID}>
        <View style={[styles.webSwitchThumb, value ? styles.webSwitchThumbActive : styles.webSwitchThumbInactive]} />
      </Pressable>
    );
  }

  return (
    <View style={styles.nativeSwitchContainer}>
      <Switch
        disabled={disabled}
        testID={testID}
        onValueChange={onValueChange}
        style={styles.switchControl}
        thumbColor={colors.neutral.cotton}
        trackColor={{ false: colors.disabled.smoke, true: colors.primary.carnation }}
        value={value}
      />
      {disabled ? <View pointerEvents="none" style={styles.nativeSwitchDisabledOverlay} testID={`${testID}-disabled-overlay`} /> : null}
    </View>
  );
}

function GGNotificationSettings(): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [localSettings, setLocalSettings] = useState<NotificationPreferencesResponse | null>(null);
  const [updatingKey, setUpdatingKey] = useState<NotificationSettingKey | null>(null);
  const { data, isLoading: isFetchingPreferences } = fetchNotificationPreferences();
  const notificationPreferences = data;
  const responseSettings = getNotificationPreferencesStateFromResponse(notificationPreferences);
  const settings = useMemo(
    () => localSettings ?? responseSettings ?? INITIAL_NOTIFICATION_SETTINGS,
    [localSettings, responseSettings],
  );
  const isInitialPreferencesLoading = isFetchingPreferences || !notificationPreferences;
  const { mutate: updatePreferences } = updateNotificationPreferences({
    onSuccess: () => undefined,
  });

  const onToggle = (key: NotificationSettingKey) => (value: boolean) => {
    const previousSettings = settings;
    const nextSettings = updateNotificationSetting(previousSettings, key, value);

    setUpdatingKey(key);
    setLocalSettings(nextSettings);
    queryClient.setQueryData(['notification_preferences'], nextSettings);
    updatePreferences(getUpdateNotificationPreferencesPayload(nextSettings), {
      onError: () => {
        queryClient.setQueryData(['notification_preferences'], previousSettings);
        setLocalSettings(previousSettings);
        setUpdatingKey(null);
      },
      onSuccess: () => {
        setLocalSettings(null);
        setUpdatingKey(null);
      },
    });
  };

  return (
    <View testID="notification-settings-screen" style={styles.container}>
      <View style={styles.section}>
        <Text color={colors.neutral.charcoal} style={styles.sectionTitle} variant="lg-bold">
          {t('notifications.settingsSections.message')}
        </Text>
        <View style={styles.row}>
          <Text color={colors.neutral.charcoal} variant="md-normal">
            {t('notifications.settingsItems.pushNotification')}
          </Text>
          <SettingsSwitch
            disabled={updatingKey === 'pushNotifications'}
            isLoading={isInitialPreferencesLoading}
            testID="notification-setting-pushNotifications"
            onValueChange={onToggle('pushNotifications')}
            value={settings.pushNotifications}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text color={colors.neutral.charcoal} style={styles.sectionTitle} variant="lg-bold">
          {t('notifications.settingsSections.information')}
        </Text>
        <View style={styles.row}>
          <Text color={colors.neutral.charcoal} variant="md-normal">
            {t('notifications.settingsItems.transaction')}
          </Text>
          <SettingsSwitch
            disabled={updatingKey === 'transactionNotifications'}
            isLoading={isInitialPreferencesLoading}
            testID="notification-setting-transactionNotifications"
            onValueChange={onToggle('transactionNotifications')}
            value={settings.transactionNotifications}
          />
        </View>
        <View style={styles.row}>
          <Text color={colors.neutral.charcoal} variant="md-normal">
            {t('notifications.settingsItems.ewaStatus')}
          </Text>
          <SettingsSwitch
            disabled={updatingKey === 'ewaStatusNotifications'}
            isLoading={isInitialPreferencesLoading}
            testID="notification-setting-ewaStatusNotifications"
            onValueChange={onToggle('ewaStatusNotifications')}
            value={settings.ewaStatusNotifications}
          />
        </View>
        <View style={[styles.row, styles.mandatoryRow]}>
          <View style={styles.mandatoryTextContainer}>
            <Text color={colors.neutral.charcoal} variant="md-normal">
              {t('notifications.settingsItems.security')}
            </Text>
            <Text color={colors.neutral.darkMist} variant="xsm-normal">
              {t('notifications.settingsItems.securityHelper')}
            </Text>
          </View>
          <SettingsSwitch
            disabled
            isLoading={isInitialPreferencesLoading}
            testID="notification-setting-securityNotifications"
            onValueChange={onToggle('securityNotifications')}
            value
          />
        </View>
      </View>
    </View>
  );
}

export default GGNotificationSettings;
